"use client";

import { c as _c } from "react/compiler-runtime";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useCallback, useState } from "react";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import WaveDropActions from "../WaveDropActions";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import DropMinimalIdentityRow from "../DropMinimalIdentityRow";
import ParticipationDropContainer from "./ParticipationDropContainer";
import ParticipationDropHeader from "./ParticipationDropHeader";
import ParticipationDropContent from "./ParticipationDropContent";
import ParticipationDropMetadata from "./ParticipationDropMetadata";
import ParticipationDropFooter from "./ParticipationDropFooter";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
import type { DropContentPresentation } from "../dropContentPresentation";
import type { DropIdentityMode, DropInteractionParams, DropLocation } from "../drop.types";
interface OngoingParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
export default function OngoingParticipationDrop(t0) {
  const $ = _c(90);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick,
    identityMode: t1,
    showInteractions: t2,
    contentPresentation: t3,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const showInteractions = t2 === undefined ? true : t2;
  const contentPresentation = t3 === undefined ? "default" : t3;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  const hasTouch = useIsTouchDevice() || isMobile;
  let identityProfile;
  let t4;
  let visibleMetadata;
  if ($[0] !== drop.author || $[1] !== drop.metadata || $[2] !== drop.wave) {
    identityProfile = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    let t5;
    if ($[6] !== drop.metadata || $[7] !== drop.wave) {
      t5 = getParticipationVisibleMetadata({
        wave: drop.wave,
        metadata: drop.metadata
      });
      $[6] = drop.metadata;
      $[7] = drop.wave;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    visibleMetadata = t5;
    t4 = identityProfile ? areSameProfileIdentity({
      left: drop.author,
      right: identityProfile
    }) : false;
    $[0] = drop.author;
    $[1] = drop.metadata;
    $[2] = drop.wave;
    $[3] = identityProfile;
    $[4] = t4;
    $[5] = visibleMetadata;
  } else {
    identityProfile = $[3];
    t4 = $[4];
    visibleMetadata = $[5];
  }
  const isSelfNominee = t4;
  const showIdentity = identityMode !== "hidden";
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  let t5;
  if ($[9] !== hasTouch || $[10] !== showInteractions) {
    t5 = () => {
      if (!showInteractions || !hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[9] = hasTouch;
    $[10] = showInteractions;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleLongPress = t5;
  let t6;
  if ($[12] !== activePartIndex || $[13] !== drop || $[14] !== onReply) {
    t6 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[12] = activePartIndex;
    $[13] = drop;
    $[14] = onReply;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleOnReply = t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsSlideUp(false);
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleOnAddReaction = t7;
  let t8;
  if ($[17] !== canShowVote || $[18] !== drop || $[19] !== showInteractions) {
    t8 = canShowVote && showInteractions ? <VotingModalButton drop={drop} onClick={() => setIsVotingModalOpen(true)} /> : null;
    $[17] = canShowVote;
    $[18] = drop;
    $[19] = showInteractions;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const voteAction = t8;
  let t9;
  if ($[21] !== activePartIndex || $[22] !== drop || $[23] !== handleOnReply || $[24] !== isMobile || $[25] !== showInteractions || $[26] !== showReplyAndQuote) {
    t9 = !isMobile && showInteractions && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[21] = activePartIndex;
    $[22] = drop;
    $[23] = handleOnReply;
    $[24] = isMobile;
    $[25] = showInteractions;
    $[26] = showReplyAndQuote;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== drop || $[29] !== showIdentity) {
    t10 = showIdentity && <WaveDropAuthorPfp drop={drop} />;
    $[28] = drop;
    $[29] = showIdentity;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== drop || $[32] !== identityMode || $[33] !== showIdentity || $[34] !== showWaveInfo) {
    t11 = showIdentity && (identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} /> : <ParticipationDropHeader drop={drop} showWaveInfo={showWaveInfo} />);
    $[31] = drop;
    $[32] = identityMode;
    $[33] = showIdentity;
    $[34] = showWaveInfo;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== activePartIndex || $[37] !== contentPresentation || $[38] !== drop || $[39] !== embedDepth || $[40] !== embedPath || $[41] !== handleLongPress || $[42] !== maxEmbedDepth || $[43] !== onDropContentClick || $[44] !== onQuoteClick || $[45] !== quotePath) {
    t12 = <ParticipationDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} contentPresentation={contentPresentation} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[36] = activePartIndex;
    $[37] = contentPresentation;
    $[38] = drop;
    $[39] = embedDepth;
    $[40] = embedPath;
    $[41] = handleLongPress;
    $[42] = maxEmbedDepth;
    $[43] = onDropContentClick;
    $[44] = onQuoteClick;
    $[45] = quotePath;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== t11 || $[48] !== t12) {
    t13 = <div className="tw-flex tw-w-full tw-flex-col">{t11}{t12}</div>;
    $[47] = t11;
    $[48] = t12;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== t10 || $[51] !== t13) {
    t14 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-px-4 tw-pt-4 tw-text-left">{t10}{t13}</div>;
    $[50] = t10;
    $[51] = t13;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== drop.id || $[54] !== identityProfile || $[55] !== isSelfNominee || $[56] !== showIdentity) {
    t15 = identityProfile && <div className={`${showIdentity ? "tw-ml-[3.25rem]" : ""} tw-px-4`}><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" showIdentityHeader={!isSelfNominee} /></div>;
    $[53] = drop.id;
    $[54] = identityProfile;
    $[55] = isSelfNominee;
    $[56] = showIdentity;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== drop.id || $[59] !== visibleMetadata) {
    t16 = <ParticipationDropMetadata metadata={visibleMetadata} contextId={drop.id} />;
    $[58] = drop.id;
    $[59] = visibleMetadata;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== drop || $[62] !== showInteractions || $[63] !== voteAction) {
    t17 = <ParticipationDropFooter drop={drop} voteAction={voteAction} showInteractions={showInteractions} />;
    $[61] = drop;
    $[62] = showInteractions;
    $[63] = voteAction;
    $[64] = t17;
  } else {
    t17 = $[64];
  }
  let t18;
  if ($[65] !== t15 || $[66] !== t16 || $[67] !== t17) {
    t18 = <div className="tw-flex tw-w-full tw-flex-col">{t15}{t16}{t17}</div>;
    $[65] = t15;
    $[66] = t16;
    $[67] = t17;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== drop || $[70] !== isMobileScreen || $[71] !== isVotingModalOpen || $[72] !== showInteractions) {
    t19 = showInteractions && (isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />);
    $[69] = drop;
    $[70] = isMobileScreen;
    $[71] = isVotingModalOpen;
    $[72] = showInteractions;
    $[73] = t19;
  } else {
    t19 = $[73];
  }
  let t20;
  if ($[74] !== drop || $[75] !== handleOnReply || $[76] !== isSlideUp || $[77] !== longPressTriggered || $[78] !== showInteractions || $[79] !== showReplyAndQuote) {
    t20 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[74] = drop;
    $[75] = handleOnReply;
    $[76] = isSlideUp;
    $[77] = longPressTriggered;
    $[78] = showInteractions;
    $[79] = showReplyAndQuote;
    $[80] = t20;
  } else {
    t20 = $[80];
  }
  let t21;
  if ($[81] !== drop || $[82] !== isActiveDrop || $[83] !== location || $[84] !== t14 || $[85] !== t18 || $[86] !== t19 || $[87] !== t20 || $[88] !== t9) {
    t21 = <ParticipationDropContainer drop={drop} isActiveDrop={isActiveDrop} location={location}>{t9}{t14}{t18}{t19}{t20}</ParticipationDropContainer>;
    $[81] = drop;
    $[82] = isActiveDrop;
    $[83] = location;
    $[84] = t14;
    $[85] = t18;
    $[86] = t19;
    $[87] = t20;
    $[88] = t9;
    $[89] = t21;
  } else {
    t21 = $[89];
  }
  return t21;
}